// Generated by GitHub Copilot
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { getTheme, setTheme } from "../utils/theme";
import ThemeToggle from "./ThemeToggle";

const NAV_ITEMS = [
    { to: "/", label: "Home", end: true },
    { to: "/about", label: "About" },
    { to: "/projects", label: "Projects" },
    { to: "/medium", label: "Medium" },
];

/**
 * Resolve the currently effective visual theme.
 *
 * @param {"light" | "dark" | "system"} themeMode Saved theme mode.
 * @returns {"light" | "dark"} Effective theme used by the UI.
 */
function resolveThemeMode(themeMode) {
    if (themeMode === "light" || themeMode === "dark") {
        return themeMode;
    }

    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
        return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }

    return "light";
}

/**
 * Build navigation link classes based on whether the route is currently active.
 *
 * @param {{isActive: boolean}} props Router state object.
 * @returns {string} Tailwind class string.
 */
function getNavLinkClassName({ isActive }) {
    const baseClasses =
        "rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] transition md:px-4";

    if (isActive) {
        return `${baseClasses} glass-chip text-[var(--text-primary)]`;
    }

    return `${baseClasses} text-[var(--text-secondary)] hover:text-[var(--text-primary)]`;
}

/**
 * Render the sticky top navigation for route-based portfolio pages.
 *
 * @returns {JSX.Element} Header with route links.
 */
function StickyHeader() {
    const [themeMode, setThemeMode] = useState(() => getTheme());
    const [resolvedTheme, setResolvedTheme] = useState(() => resolveThemeMode(getTheme()));

    useEffect(() => {
        const currentTheme = getTheme();
        setThemeMode(currentTheme);
        setTheme(currentTheme);
        setResolvedTheme(resolveThemeMode(currentTheme));
    }, []);

    /**
     * Toggle between light and dark themes.
     */
    function handleThemeToggle() {
        const nextTheme = resolvedTheme === "dark" ? "light" : "dark";
        setTheme(nextTheme);
        setThemeMode(nextTheme);
        setResolvedTheme(nextTheme);
    }

    const isDarkTheme = resolvedTheme === "dark";
    const themeToggleLabel = isDarkTheme ? "Switch to light theme" : "Switch to dark theme";
    const themeToggleHint =
        themeMode === "system"
            ? "System theme is currently active. Toggling will set an explicit theme."
            : "Toggle between light and dark theme";

    return (
        <header className="sticky top-0 z-40 px-4 pt-4 md:px-8">
            <div className="glass-card mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3 md:px-6">
                <NavLink
                    to="/"
                    end
                    className="rounded-full px-3 py-1.5 text-sm font-bold tracking-[0.08em] text-[rgba(var(--accent-rgb),0.95)]"
                    aria-label="Go to home page"
                >
                    RamDragneel PORTFOLIO
                </NavLink>

                <nav className="flex flex-wrap items-center gap-1.5" aria-label="Primary">
                    {NAV_ITEMS.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={Boolean(item.end)}
                            className={getNavLinkClassName}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Theme switcher">
                    <ThemeToggle
                        checked={isDarkTheme}
                        onToggle={handleThemeToggle}
                        label={themeToggleLabel}
                        hint={themeToggleHint}
                    />
                </div>
            </div>
        </header>
    );
}

export default StickyHeader;
